import type { DateTime } from "luxon";
import { buildCanonicalFanOutMap, computeWindowOverlapMinutes, isContained, matchesTimeGroup } from "../../../utils/timeGrouping";
import type { ScheduleRow, ShiftTimeAnchor } from "../types";
import { formatHourMinute, shiftWindowKey } from "../util/shiftTimeFormat";
import { groupRowsByCanonicalShiftTime, type ShiftTimeCanonicalResult } from "./shiftTimeCanonical";

const MINUTES_IN_DAY = 1440;

/** A deduped anchor window keyed by its `"HH:MM-HH:MM"` label, carrying its popularity weight. */
interface AnchorWindow {
	key: string;
	startMin: number;
	endMin: number;
	ratio: number;
}

/** `"HH:MM-HH:MM"` key for a minute-of-day window, matching the §6.2 START_TIME key format. */
const anchorKeyFromMinutes = (startMin: number, endMin: number): string =>
	`${formatHourMinute(Math.floor(startMin / 60), startMin % 60)}-${formatHourMinute(Math.floor(endMin / 60), endMin % 60)}`;

/** Window duration in minutes, handling overnight wrap (`endMin <= startMin`). */
const windowDuration = (startMin: number, endMin: number): number => (endMin > startMin ? endMin - startMin : endMin + MINUTES_IN_DAY - startMin);

/** Minute-of-day in the department timezone (0..1439). */
const toMinuteOfDay = (dt: DateTime, timezone: string): number => {
	const zoned = dt.setZone(timezone);
	return zoned.hour * 60 + zoned.minute;
};

/**
 * Dedupe anchors by identical window, summing ratios (the same window staffed by several
 * role-groups/jobs is more popular), then order by **ratio desc, then duration desc**. Ratio-desc is
 * the priority `buildCanonicalFanOutMap` expects (most-common first); the duration-desc tiebreak
 * preserves the containment precedence so a wider anchor wins the canonical position over a narrower
 * one it contains (same load-bearing tiebreak as `groupRowsByCanonicalShiftTime`).
 */
const dedupeAndRankAnchors = (anchors: ShiftTimeAnchor[]): AnchorWindow[] => {
	const byKey = new Map<string, AnchorWindow>();
	for (const anchor of anchors) {
		const key = anchorKeyFromMinutes(anchor.startMin, anchor.endMin);
		const existing = byKey.get(key);
		if (existing) existing.ratio += anchor.ratio;
		else byKey.set(key, { key, startMin: anchor.startMin, endMin: anchor.endMin, ratio: anchor.ratio });
	}
	return [...byKey.values()].sort((a, b) => b.ratio - a.ratio || windowDuration(b.startMin, b.endMin) - windowDuration(a.startMin, a.endMin));
};

/**
 * Group rows by Shift Time using the department's stored popular windows as **fixed anchors**
 * instead of deriving anchors from per-row popularity (the alternative to
 * {@link groupRowsByCanonicalShiftTime}, enabled by the "align group times to popular times" setting).
 *
 * 1. Dedupe + rank anchors by ratio desc (duration desc tiebreak).
 * 2. Canonicalize anchors among themselves via the shared `buildCanonicalFanOutMap` — anchors that
 *    contain/sit near each other collapse to "leaf" anchors (identical semantics to the popularity path).
 * 3. Route each row to its leaf anchor(s), reusing the existing overlap primitives:
 *    - covers ≥2 leaves → fan out into each (wide-shift spread);
 *    - covers exactly 1 leaf → that leaf (single-cover fold);
 *    - else matches ≥1 leaf (proximity/containment) → best by ratio, then max overlap;
 *    - else → max-overlap leaf, or its own exact-window group if it overlaps none (never dropped).
 * 4. Drop any anchor that received no rows.
 *
 * Returns the same {@link ShiftTimeCanonicalResult} shape so the caller branches in one line.
 * Falls back to popularity grouping when there are no anchors (e.g. a department with no history).
 */
export const groupRowsByDepartmentAnchors = (rows: ScheduleRow[], anchors: ShiftTimeAnchor[], day: DateTime, timezone: string): ShiftTimeCanonicalResult => {
	const ranked = dedupeAndRankAnchors(anchors);
	if (ranked.length === 0) return groupRowsByCanonicalShiftTime(rows);

	// Collapse anchors that contain/neighbor each other; the surviving "leaf" anchors are the
	// destinations rows route into (ratio-desc order preserved from `ranked`).
	const fanOut = buildCanonicalFanOutMap(ranked);
	const leafKeys = new Set([...fanOut.values()].flat());
	const leaves = ranked.filter((anchor) => leafKeys.has(anchor.key));

	const groups = new Map<string, ScheduleRow[]>();
	const canonicalStart = new Map<string, DateTime>();
	const canonicalEnd = new Map<string, DateTime>();

	// Materialize each leaf anchor's time-of-day onto the printout day (overnight end rolls to the
	// next day) for §5.7 section sorting.
	const dayStart = day.setZone(timezone).startOf("day");
	for (const leaf of leaves) {
		canonicalStart.set(leaf.key, dayStart.plus({ minutes: leaf.startMin }));
		const overnight = leaf.endMin <= leaf.startMin;
		canonicalEnd.set(leaf.key, dayStart.plus({ days: overnight ? 1 : 0, minutes: leaf.endMin }));
	}

	const addRow = (key: string, row: ScheduleRow): void => {
		const existing = groups.get(key);
		if (existing) existing.push(row);
		else groups.set(key, [row]);
	};

	// Walk rows in their incoming (Phase 2 sorted) order so name/sort ordering is preserved within
	// each anchor group.
	for (const row of rows) {
		const rowWindow = { startMin: toMinuteOfDay(row.shiftStart, timezone), endMin: toMinuteOfDay(row.shiftEnd, timezone) };
		const covered = leaves.filter((leaf) => isContained(leaf, rowWindow));

		if (covered.length >= 2) {
			for (const leaf of covered) addRow(leaf.key, row);
			continue;
		}
		if (covered.length === 1) {
			addRow(covered[0].key, row);
			continue;
		}

		// `leaves` is already ratio-desc, so the first match is the highest-ratio anchor; the overlap
		// tiebreak only separates equal-ratio anchors.
		const matched = leaves.filter((leaf) => matchesTimeGroup(rowWindow, leaf));
		if (matched.length > 0) {
			const best = matched.reduce((a, b) =>
				b.ratio > a.ratio || (b.ratio === a.ratio && computeWindowOverlapMinutes(rowWindow, b) > computeWindowOverlapMinutes(rowWindow, a)) ? b : a,
			);
			addRow(best.key, row);
			continue;
		}

		// No proximity/containment match — snap to the most-overlapping anchor; if it overlaps none,
		// keep its own exact-window group so the row is never dropped.
		let bestLeaf: AnchorWindow | undefined;
		let bestOverlap = 0;
		for (const leaf of leaves) {
			const overlap = computeWindowOverlapMinutes(rowWindow, leaf);
			if (overlap > bestOverlap) {
				bestOverlap = overlap;
				bestLeaf = leaf;
			}
		}
		if (bestLeaf) {
			addRow(bestLeaf.key, row);
		} else {
			const ownKey = shiftWindowKey(row.shiftStart, row.shiftEnd);
			if (!canonicalStart.has(ownKey)) {
				canonicalStart.set(ownKey, row.shiftStart);
				canonicalEnd.set(ownKey, row.shiftEnd);
			}
			addRow(ownKey, row);
		}
	}

	// Drop anchors that ended up empty (groups only ever holds keys with ≥1 row; prune their leftover
	// canonical times for cleanliness).
	for (const key of [...canonicalStart.keys()]) {
		if (!groups.has(key)) {
			canonicalStart.delete(key);
			canonicalEnd.delete(key);
		}
	}

	return { groups, canonicalStart, canonicalEnd };
};
